import { useContext, useEffect, useState } from "react";

import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Box } from "@mui/material";
import { useArcCoCountyYieldOverrides } from "api-toolkit/useArcCoCountyYieldOverrides";
import { UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { getUniqueCounties } from "components/Vault/Farm/helpers";
import WorkspaceActionBar from "components/Workspaces/WorkspaceActionBar";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import WorkspacePageHeader from "components/Workspaces/WorkspacePageHeader";
import _ from "lodash";
import { useSnackbar } from "notistack";

import { useChatAssistantAttachments } from "../../contexts/useChatAssistantAttachments";
import { ArcPlcIcon } from "../Icons/Icons";
import ArcPlcFarmCountyCard from "./components/ArcPlcFarmCountyCard";

export default function ArcPlcCountyListWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const { addAttachments } = useChatAssistantAttachments();
  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent } = useDialog();

  const [currentYear, setCurrentYear] = useState(globalState?.crop_year);
  const { arcCoCountyYieldOverrides } = useArcCoCountyYieldOverrides({ crop_year: currentYear });

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const [baseAcres, setBaseAcres] = useState([]);
  const [isLoadingBaseAcres, setIsLoadingBaseAcres] = useState(true);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadProducerFarmBaseAcres = async () => {
    try {
      setIsLoadingBaseAcres(true);

      const producerFilter = {
        producer_token: globalState.producer_token,
        crop_year: currentYear,
        is_active: true,
      };
      const _baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(producerFilter, globalState);
      setBaseAcres(_baseAcres);

      setIsLoadingBaseAcres(false);
    } catch (err) {
      setIsLoadingBaseAcres(false);
      setBaseAcres([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (currentYear && globalState?.producer_token) {
      loadProducerFarmBaseAcres();
      setBaseAcres([]);
      setCountyPractices([]);
      setUnionCountyPractices([]);
    }
  }, [currentYear, globalState?.producer_token]);

  // --- --- ---

  const [countyPractices, setCountyPractices] = useState([]);
  const [unionCountyPractices, setUnionCountyPractices] = useState([]);
  const [isLoadingCountyPractices, setIsLoadingCountyPractices] = useState(true);

  const loadCountyPractices = async () => {
    try {
      setIsLoadingCountyPractices(true);

      const _countyPractices = getUniqueCounties(baseAcres);
      setCountyPractices(_countyPractices);
      setIsLoadingCountyPractices(false);
    } catch (err) {
      setCountyPractices(null);
      setIsLoadingCountyPractices(false);

      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length) {
      loadCountyPractices();
    } else {
      setCountyPractices(null);
      setIsLoadingCountyPractices(false);
    }
  }, [globalState?.producer_token, baseAcres]);

  useEffect(() => {
    const loadUnionProducerFarmBaseAcres = async () => {
      // Union:
      const _baseAcresUnion = structuredClone(countyPractices);
      _baseAcresUnion.forEach((currentCountyPractice) => {
        currentCountyPractice.BaseAcres = baseAcres.filter(
          (ba) =>
            ba.location_state_name === currentCountyPractice.location_state_name &&
            ba.location_county_name === currentCountyPractice.location_county_name
        );

        currentCountyPractice.CropProfiles = _.uniqBy(
          currentCountyPractice?.BaseAcres,
          (pfc) => pfc.commodity_name
        )?.sort((a, b) => a.commodity_name?.localeCompare(b.commodity_name));

        currentCountyPractice.CropProfiles_Sort = currentCountyPractice.CropProfiles?.reduce(
          (accumulator, currentValue) => (accumulator?.length > 0 ? ", " : "") + currentValue?.commodity_name,
          ""
        );
      });
      setUnionCountyPractices(_baseAcresUnion);
    };
    if (countyPractices && baseAcres) {
      loadUnionProducerFarmBaseAcres();
    }
  }, [globalState?.producer_token, countyPractices, baseAcres]);

  useEffect(() => {
    addAttachments([{ name: "ARC/PLC Calculator", content: unionCountyPractices }]);
  }, [unionCountyPractices]);

  return (
    <>
      <WorkspaceHeader
        title="ARC/PLC Calculator"
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
      />

      <WorkspacePageHeader
        title="Projected Payments"
        currentYear={currentYear}
        onYearChange={setCurrentYear}
        isLoading={isLoadingBaseAcres || isLoadingCountyPractices}
      />

      <Box className="cardContainer">
        {Boolean(unionCountyPractices?.length) &&
          unionCountyPractices.map((currentCountyPractice, index) => (
            <ArcPlcFarmCountyCard
              key={index}
              currentCounty={currentCountyPractice}
              crop_year={currentYear}
              arcCoCountyYieldOverrides={arcCoCountyYieldOverrides}
            />
          ))}

        {!countyPractices?.length && (
          <EmptyCard
            text="No farms listed"
            secondaryText="Add farms and base acres to your Farm Vault"
            isLoading={isLoadingBaseAcres || isLoadingCountyPractices}
          />
        )}
      </Box>

      {confirmDialogComponent}
      <WorkspaceActionBar content={<></>} />
    </>
  );
}
